import React, { useState } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { Theme, useTheme } from '@mui/material/styles';
import {
  Box,
  Dialog,
  Divider,
  Grow,
  Link as MUILink,
  Switch,
  Typography,
} from '@mui/material';
import Link from 'next/link';
import IconButton from '@mui/material/IconButton';
import PremiumIcon from '/public/icons/common/premium-plan.svg';
import PremiumIcon2x from '/public/icons/common/premium-plan-2x.svg';
import NonPremiumIcon from '/public/icons/common/free-plan.svg';
import { ChevronRight } from '@mui/icons-material';
import ChevronDown from '/public/icons/chevron-down.svg';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { format } from 'date-fns';
import Pastpayments from './PastPayments';
import CancelSusbscriptionDialog from './CancelSusbscriptionDialog';
import {
  PaymentTypeEnum,
  RenewalPolicyEnum,
} from '../../enums/UserCurrentEnums';
import { useRouter } from 'next/router';
import { usePlanQuery } from '../../redux/services/payments/payments.generated';
import { Plan } from '../../redux/types.generated';
import { useDispatch, useSelector } from 'react-redux';
import {
  getPlayPraharAfterPlaylist,
  setPlayPraharAfterPlaylist,
} from '../../redux/slices/playerSlice';
import PraharUnselected from '/public/icons/nav-icons/prahar-unselected.svg';

interface AccountSidebarProps {
  breakpoint: boolean;
  userData: any;
  isDrawerOpen: boolean;
  toggleDrawer: (
    open: boolean
  ) => (event: React.KeyboardEvent | React.MouseEvent) => void;
  onLogout: () => void;
  onCancelSubscription: () => void;
}

const AccountSidebar: React.FC<AccountSidebarProps> = ({
  breakpoint,
  isDrawerOpen,
  userData,
  toggleDrawer,
  onLogout,
  onCancelSubscription,
}) => {
  const [showPastPayments, setShowPastPayments] = useState(false);
  const [showConfirmSubscriptionDialog, setShowConfirmSubscriptionDialog] =
    useState(false);
  const theme = useTheme();
  const styles = useStyles(theme);
  const router = useRouter();
  const userDetails = userData?.userCurrent;
  const activeSubscription = userDetails?.activeSubscription ?? null;
  const successfulTransactions = userDetails?.successfulTransactions ?? [];

  //queries
  const { data: planData } = usePlanQuery();

  //methods
  const togglePastPayments = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setShowPastPayments(!showPastPayments);
  };

  const handleCancelSubscription = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setShowConfirmSubscriptionDialog(true);
  };

  const cancelSubscription = () => {
    setShowConfirmSubscriptionDialog(false);
    onCancelSubscription();
  };

  const goPremium = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    toggleDrawer(false)(e);
    router.push('/go-premium');
  };

  const handleLogout = (e: React.MouseEvent<HTMLElement>) => {
    toggleDrawer(false)(e);
    e.stopPropagation();
    onLogout();
  };

  const getProfileHeader = (text: string) => (
    <Typography variant='subtitle2' fontWeight={700} sx={styles.profileHeader}>
      {text}
    </Typography>
  );

  const getProfileDetails = (text: string) => (
    <Typography variant='body1' sx={styles.profileDetails}>
      {text}
    </Typography>
  );

  const getPlanDuration = (planName: string) => {
    switch (planName) {
      case 'Monthly':
        return 'month';
      case 'Yearly':
        return 'year';
      default:
        return '';
    }
  };

  const dispatch = useDispatch();
  const playPraharAfterPlaylist = useSelector(getPlayPraharAfterPlaylist);

  const handlePlayPrahar = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setPlayPraharAfterPlaylist(event?.target.checked));
  };

  return (
    <>
      <SwipeableDrawer
        anchor='right'
        open={isDrawerOpen}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
      >
        <Box
          sx={{
            ...styles.drawerContainer,
            width: breakpoint ? '490px' : '100vw',
            paddingBottom: breakpoint ? '75px' : '',
          }}
          role='presentation'
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              my: 1,
              px: 2,
            }}
          >
            <Typography variant='h2' letterSpacing={0.6}>
              My Account
            </Typography>
            <IconButton sx={styles.iconButton} onClick={toggleDrawer(false)}>
              <CloseOutlinedIcon />
            </IconButton>
          </Box>
          <Box
            sx={[
              styles.containerPadding,
              activeSubscription?.isActive
                ? styles.premiumContainer
                : styles.nonPremiumContainer,
            ]}
          >
            <Box sx={{ display: 'flex' }}>
              <Box sx={styles.planIconContainer}>
                {activeSubscription?.isActive ? (
                  <PremiumIcon2x />
                ) : (
                  <NonPremiumIcon />
                )}
              </Box>
              <Box sx={{ mx: 1, my: 1 }}>
                <Typography
                  variant='subtitle2'
                  fontWeight={700}
                  letterSpacing={1.4}
                  sx={styles.premiumText1}
                >
                  YOUR PLAN
                </Typography>
                <Typography
                  variant='h2'
                  letterSpacing={0.22}
                  fontSize={22}
                  sx={styles.premiumText2}
                >
                  {activeSubscription?.isActive ? 'Premium' : 'Free'}
                </Typography>
                {activeSubscription?.isActive && (
                  <Typography variant='subtitle1' sx={styles.premiumText3}>
                    till{' '}
                    {format(
                      new Date(activeSubscription?.expiresAt),
                      'do MMMM yyyy'
                    )}
                  </Typography>
                )}
              </Box>
            </Box>
            {/* for non premium users */}
            {!activeSubscription?.isActive && (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box onClick={goPremium} sx={styles.goPremiumButtonContainer}>
                  <Box sx={styles.goPremiumTextContainer}>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant='body2'
                        component='span'
                        fontWeight={700}
                      >
                        Premium{' '}
                        <Typography
                          variant='h4'
                          component='span'
                          letterSpacing={0.5}
                        >
                          @{' '}
                        </Typography>
                      </Typography>

                      {planData?.plans.map((plan: Plan, index: number) => (
                        <Typography
                          variant='h4'
                          component='span'
                          key={plan?.id}
                          letterSpacing={0.5}
                        >
                          {`${plan?.currencySymbol}${
                            plan?.price
                          }/${getPlanDuration(plan?.name)}${
                            index < planData?.plans?.length - 1 ? ', ' : ''
                          }`}
                        </Typography>
                      ))}
                    </Box>
                    <Box>
                      <Typography component='span' variant='h2'>
                        Unlock
                      </Typography>
                    </Box>
                    <Box>
                      <ChevronRight sx={styles.unlockIcon} />
                    </Box>
                  </Box>
                </Box>{' '}
                <Box sx={{ display: 'flex', mb: 1 }}>
                  <PremiumIcon
                    width={20}
                    height={20}
                    style={{ marginRight: 8, alignSelf: 'center' }}
                  />
                  <Link href='/go-premium' passHref>
                    <MUILink
                      component='a'
                      target='_blank'
                      rel='noopener noreferrer'
                      variant='subtitle1'
                      fontWeight={400}
                      color={theme.ragyaPalette.gray3}
                      sx={{ display: 'inline-block' }}
                    >
                      View subscription benefits
                    </MUILink>
                  </Link>
                </Box>
              </Box>
            )}
          </Box>
          <Box sx={[styles.containerPadding, styles.profileContainer]}>
            <Box sx={{ display: 'flex', mb: 2, alignItems: 'center' }}>
              <Box
                component={PraharUnselected}
                sx={{
                  width: 24,
                  height: 24,
                  pr: '4px',
                }}
              />
              <Typography
                variant={'body2'}
                sx={{
                  color: (theme) => theme.ragyaPalette.gray4,
                }}
              >
                Play{' '}
                <Typography component={'span'} variant={'body2'}>
                  prahar
                </Typography>{' '}
                after playlists
              </Typography>
              <Switch
                checked={playPraharAfterPlaylist}
                onChange={handlePlayPrahar}
              />
            </Box>
            <Box sx={styles.profileItemContainer}>
              {getProfileHeader('NAME')}
              {getProfileDetails(
                `${userDetails?.firstName} ${userDetails?.lastName}`
              )}
            </Box>
            <Box sx={[styles.profileItemContainer, { pb: 0 }]}>
              {getProfileHeader('E-MAIL ID')}
              {getProfileDetails(`${userDetails?.email} `)}
            </Box>
            {/* Todo: uncomment when we have billing address coming from backend */}
            {/* <Box sx={styles.profileItemContainer}>
            {getProfileHeader('BILLING ADDRESS')}
          </Box> */}
            {/* Todo: enable this button when we have design and implementaion for edit profile */}
            {/* <Box sx={[styles.profileItemContainer, { pb: 0 }]}>
            <Box sx={styles.editButtonContainer}>
              <Typography
                variant='body2'
                fontWeight={700}
                color={theme.ragyaPalette.gray3}
              >
                edit profile
              </Typography>
            </Box>
          </Box> */}
          </Box>
          {!!successfulTransactions?.length && (
            <>
              <Divider sx={{ backgroundColor: theme.ragyaPalette.gray10 }} />
              <Box
                onClick={togglePastPayments}
                sx={[styles.buttonContainer, styles.containerPadding]}
              >
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    variant='body1'
                    color={theme.ragyaPalette.gray0}
                    sx={{ flex: 1 }}
                  >
                    Past Payments
                  </Typography>
                  {showPastPayments ? (
                    <ChevronDown sx={{ color: theme.ragyaPalette.gray4 }} />
                  ) : (
                    <ChevronRight sx={{ color: theme.ragyaPalette.gray4 }} />
                  )}
                </Box>
                {showPastPayments && (
                  <>
                    <Divider sx={{ pb: 1 }} />
                    <Box>
                      <Pastpayments
                        successfulTransactions={successfulTransactions}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </>
          )}

          <Divider sx={{ borderColor: theme.ragyaPalette.gray10 }} />
          {!activeSubscription?.isActive && (
            <Link href={'/redeem'} passHref>
              <Box sx={[styles.buttonContainer, styles.containerPadding]}>
                <Typography variant='body1' color={'primary'}>
                  Redeem a gift
                </Typography>
              </Box>
            </Link>
          )}
          <Divider sx={{ borderColor: theme.ragyaPalette.gray10 }} />

          {activeSubscription?.isActive &&
            activeSubscription?.subscriptionPlan?.paymentType ===
              PaymentTypeEnum.recurring &&
            activeSubscription.renewalPolicy !==
              RenewalPolicyEnum.cancelled && (
              <Box
                onClick={handleCancelSubscription}
                sx={[styles.buttonContainer, styles.containerPadding]}
              >
                <Typography variant='body1' color={theme.ragyaPalette.errorRed}>
                  Cancel Subscription
                </Typography>
              </Box>
            )}
          <Divider sx={{ backgroundColor: theme.ragyaPalette.gray10 }} />
          <Box
            onClick={handleLogout}
            sx={[styles.buttonContainer, styles.containerPadding]}
          >
            <Typography variant='body1' color={theme.ragyaPalette.gray0}>
              Log Out
            </Typography>
          </Box>
          <Divider sx={{ borderColor: theme.ragyaPalette.gray10 }} />
          <Link href={'/delete-account'} passHref>
            <Box sx={[styles.buttonContainer, styles.containerPadding]}>
              <Typography variant='body1' color={theme.ragyaPalette.errorRed}>
                Delete Account
              </Typography>
            </Box>
          </Link>
          <Divider sx={{ borderColor: theme.ragyaPalette.gray10 }} />
        </Box>
      </SwipeableDrawer>
      <Dialog
        open={showConfirmSubscriptionDialog}
        PaperProps={{
          sx: styles.subscriptionDialogPaper,
        }}
        TransitionComponent={Grow}
        maxWidth={'xs'}
      >
        <CancelSusbscriptionDialog
          activeSubscription={activeSubscription}
          setShowConfirmSubscriptionDialog={setShowConfirmSubscriptionDialog}
          onCancelSubscription={cancelSubscription}
        />
      </Dialog>
    </>
  );
};

export default AccountSidebar;
const useStyles = (theme: Theme) => ({
  drawerContainer: {
    flex: 1,
    py: 0,
    backgroundColor: theme.ragyaPalette.gray13,
  },
  drawerHeaderContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    my: 1,
    px: 2,
  },
  premiumContainer: {
    backgroundColor: theme.ragyaPalette.orange5,
  },
  nonPremiumContainer: {
    backgroundColor: theme.ragyaPalette.darkGreen,
  },
  goPremiumButtonContainer: {
    background: `linear-gradient(90deg, #A82C00 0%, ${theme.palette.primary.main} 100%)`,
    borderRadius: 32,
    color: theme.ragyaPalette.gray0,
    m: 1,
    mb: 2,
    ml: 0,
    px: '20px',
    cursor: 'pointer',
  },
  goPremiumTextContainer: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
  },
  unlockIcon: { paddingTop: '6px', height: 36 },
  planIconContainer: {
    alignSelf: 'center',
  },
  buttonContainer: {
    cursor: 'pointer',
    '&:hover': {
      transition: '0.2s ease-in',
      background: 'rgba(255, 255, 255, 0.05)',
    },
  },
  profileContainer: { py: 3 },
  profileItemContainer: {
    pb: 3,
  },
  profileHeader: {
    color: theme.ragyaPalette.gray6,
  },
  profileDetails: {
    color: theme.ragyaPalette.gray0,
  },
  editButtonContainer: {
    border: '1px solid',
    borderRadius: 28,
    borderColor: theme.ragyaPalette.gray5,
    px: 2,
    py: 1,
    width: 120,
    height: 40,
  },
  containerPadding: { px: 2, py: 1, pb: 1 },
  premiumText1: { color: theme.ragyaPalette.gray4 },
  premiumText2: { color: theme.ragyaPalette.gray1 },
  premiumText3: { color: theme.ragyaPalette.gray3 },
  iconButton: { color: theme.ragyaPalette.gray4 },
  subscriptionDialogPaper: {
    backgroundImage: 'none',
    backgroundColor: theme.ragyaPalette.gray12,
    p: 3,
    m: 1,
    borderRadius: '8px',
  },
});
